// src/components/Aboutus.jsx
import { motion, MotionConfig } from "framer-motion";
import { Users, Trophy, BookOpen } from "lucide-react";

const features = [
  { title: "Workshops", description: "Hands-on learning with latest technologies.", icon: BookOpen },
  { title: "Networking", description: "Connect with experts and peers.", icon: Users },
  { title: "Competitions", description: "Participate in challenges to grow skills.", icon: Trophy },
];

const EASE = [0.16, 1, 0.3, 1];
const NAVY = "#00334d";
const BLUE = "#006699";
const ACCENT = "#5cc8f5";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.7, delay, ease: EASE },
});

// Dark band with an endless sliding line of type. Two identical copies, slide by half.
function Band({ children, reverse = false, duration = 30 }) {
  const copy = (k) => (
    <span key={k} className="flex shrink-0 items-center" aria-hidden="true">
      {children}
    </span>
  );
  return (
    <div className="bg-[#00334d] overflow-hidden select-none py-1.5 sm:py-2 lg:py-2.5">
      <motion.div
        className="flex w-max"
        animate={{ x: reverse ? ["-50%", "0%"] : ["0%", "-50%"] }}
        transition={{ duration, ease: "linear", repeat: Infinity }}
      >
        {copy(1)}
        {copy(2)}
      </motion.div>
    </div>
  );
}

const Dot = () => (
  <span
    className="inline-block rounded-full mx-[0.28em] w-[0.16em] h-[0.16em] flex-shrink-0"
    style={{ backgroundColor: ACCENT }}
  />
);

// White logo on a navy disc, with a slowly turning ring of text around it
function Badge() {
  const ring = "IEEE ISIMS  • STUDENT BRANCH • IEEE ISIMS  • STUDENT BRANCH • ";
  return (
    <div className="relative w-[210px] h-[210px] sm:w-[280px] sm:h-[280px] lg:w-[400px] lg:h-[400px] mx-auto">
      <motion.svg
        viewBox="0 0 300 300"
        className="absolute inset-0 w-full h-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 32, ease: "linear", repeat: Infinity }}
        aria-hidden="true"
      >
        <defs>
          <path id="about-ring" d="M150,150 m-122,0 a122,122 0 1,1 244,0 a122,122 0 1,1 -244,0" />
        </defs>
        <text fill={NAVY} fontSize="17" fontWeight="800" letterSpacing="1.5">
          <textPath href="#about-ring" textLength="760" lengthAdjust="spacing">
            {ring}
          </textPath>
        </text>
      </motion.svg>

      {/* navy disc the white logo sits on */}
      <div className="absolute inset-[17%] rounded-full bg-[#00334d] shadow-[0_18px_40px_rgba(0,51,77,0.25)]" />
      {/* ripple breathing out of the disc */}
      <motion.div
        className="absolute inset-[17%] rounded-full border-2"
        style={{ borderColor: BLUE }}
        animate={{ scale: [1, 1.22], opacity: [0.5, 0] }}
        transition={{ duration: 2.8, ease: "easeOut", repeat: Infinity }}
        aria-hidden="true"
      />

      <motion.img
        src="/logosb.png"
        alt="IEEE ISIMS SB logo"
        className="absolute inset-0 m-auto w-[40%] h-auto object-contain"
        animate={{ y: [-4, 4, -4] }}
        transition={{ duration: 5, ease: "easeInOut", repeat: Infinity }}
      />
    </div>
  );
}

export default function Aboutus() {
  const type =
    "font-black uppercase tracking-tighter leading-[1] whitespace-nowrap text-[7.5vw] sm:text-[5vw] lg:text-[min(3.6vw,56px)]";

  return (
    <MotionConfig reducedMotion="user">
      <section id="about" className="relative w-full bg-white overflow-hidden">
        <h2 className="sr-only">Who are we? We are IEEE ISIMS SB.</h2>

        {/* Top band: the question, outlined */}
        <Band duration={34}>
          {[0, 1, 2].map((i) => (
            <span key={i} className="flex items-center">
              <span
                className={`${type} text-transparent`}
                style={{ WebkitTextStroke: "1px rgba(255,255,255,0.75)" }}
              >
                Who are we?
              </span>
              <span className={type}><Dot /></span>
            </span>
          ))}
        </Band>

        {/* Body */}
        <div className="max-w-[1400px] mx-auto px-6 sm:px-12 lg:px-20 py-10 sm:py-12 lg:py-16 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">
          <motion.div
            className="lg:col-span-5 lg:order-2"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <Badge />
          </motion.div>

          <div className="lg:col-span-7 lg:order-1">
            <motion.h3 {...fadeUp(0)} className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#00334d] leading-[1.02] tracking-tight">
              Empowering Innovators
            </motion.h3>

            <motion.p {...fadeUp(0.1)} className="mt-4 lg:mt-6 text-base sm:text-lg lg:text-[22px] text-gray-600 leading-relaxed lg:leading-[1.6] max-w-3xl">
              IEEE ISIMS SB is a student branch of IEEE at the Higher Institute of Computer
              Science and Multimedia of Sfax. It aims to promote innovation, technical
              skills, and professional growth through workshops, events, competitions,
              and collaboration opportunities with industry experts.
            </motion.p>

            <ul className="mt-7 sm:mt-9 lg:mt-10 border-b border-[#006699]/15 lg:border-b-0 lg:grid lg:grid-cols-3 lg:gap-8">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <motion.li
                    key={f.title}
                    {...fadeUp(0.15 + i * 0.08)}
                    className="group relative flex items-center gap-4 sm:gap-5 py-4 sm:py-5 pl-4 border-t border-[#006699]/15 hover:bg-[#006699]/[0.04] transition-colors lg:flex-col lg:items-start lg:gap-4 lg:pl-0 lg:pt-7 lg:pb-3 lg:hover:bg-transparent"
                  >
                    <span
                      className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#006699] origin-top scale-y-0 group-hover:scale-y-100 transition-transform duration-300 lg:-top-px lg:bottom-auto lg:h-[3px] lg:w-full lg:origin-left lg:scale-y-100 lg:scale-x-0 lg:group-hover:scale-x-100"
                    />
                    <span className="flex items-center justify-center w-11 h-11 lg:w-12 lg:h-12 flex-shrink-0 border border-[#006699]/25 text-[#006699] group-hover:bg-[#006699] group-hover:text-white group-hover:border-[#006699] transition-colors">
                      <Icon className="w-5 h-5 lg:w-6 lg:h-6" strokeWidth={1.75} />
                    </span>
                    <div className="min-w-0">
                      <h4 className="text-base sm:text-lg lg:text-xl font-bold text-[#00334d]">{f.title}</h4>
                      <p className="text-sm lg:text-base text-gray-500 mt-0.5 leading-relaxed">{f.description}</p>
                    </div>
                  </motion.li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* Bottom band: the answer, solid, sliding the other way */}
        <Band reverse duration={38}>
          {[0, 1, 2].map((i) => (
            <span key={i} className="flex items-center">
              <span className={`${type} text-white`}>We are IEEE ISIMS SB</span>
              <span className={type}><Dot /></span>
            </span>
          ))}
        </Band>
      </section>
    </MotionConfig>
  );
}